import React, { useEffect, useState, useRef } from 'react';
import HeroSection from '../../components/HeroSection/HeroSection';
import styles from './Homepage.module.css';
import profilePic from '../../assets/profilePic.svg';
import SocialBtns from '../../components/SocialBtns/SocialBtns';
import { faYoutube, faInstagram, faXTwitter, faFacebookF } from "@fortawesome/free-brands-svg-icons";
import { faPlay, faVideo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AchievementsSection from '../../components/AchievementsSection/AchievementsSection';

import m1 from "../../assets/images/m1.png";
import m2 from "../../assets/images/m2.png";
import m3 from "../../assets/images/m3.png";
import m4 from "../../assets/images/m4.png";
import m5 from "../../assets/images/m5.png";
import m6 from "../../assets/images/m6.png";
import YoutubeSection from '../../components/YoutubeSection/YoutubeSection';
import { fetchInstagramStats } from '../../utils/instagramUtils';
import { fetchChannelStats } from '../../utils/youtubeUtils';
import {
  getCachedYoutubeData,
  saveYoutubeData,
  getCachedInstagramData,
  saveInstagramData
} from '../../utils/firebaseUtils';
import FAQSection from '../../components/FAQSection/FAQSection';

// --- Custom Hook for Scroll Animations ---
const useScrollReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(styles.animateIn);
          observer.unobserve(entry.target); // Triggers only once
        }
      },
      { threshold: 0.15 } // Triggers when 15% of the element is visible
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return ref;
};

const Homepage = () => {
  const CHANNEL_ID = 'UCPF4bAZimS4T8w1TlbeIAYg';
  const INSTAGRAM_USERNAME = "fasbeam";

  const [ytStats, setYtStats] = useState(null);
  const [instagramStats, setInstagramStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Animation Refs
  const founderRef = useScrollReveal();
  const numbersRef = useScrollReveal();
  const mediaRef = useScrollReveal();

  useEffect(() => {
    const loadStats = async () => {
      setIsLoadingStats(true);
      // YOUTUBE
      try {
        const cachedYoutube = await getCachedYoutubeData(CHANNEL_ID);
        if (cachedYoutube) {
          setYtStats(cachedYoutube);
        } else {
          const freshYoutube = await fetchChannelStats(CHANNEL_ID);
          if (freshYoutube) {
            await saveYoutubeData(CHANNEL_ID, freshYoutube);
            setYtStats(freshYoutube);
          }
        }
      } catch (error) {
        console.error("YouTube stats error:", error);
      }

      // INSTAGRAM
      try {
        const cachedInstagram = await getCachedInstagramData(INSTAGRAM_USERNAME);
        if (cachedInstagram) {
          setInstagramStats(cachedInstagram);
        } else {
          const freshInstagram = await fetchInstagramStats(INSTAGRAM_USERNAME);
          if (freshInstagram) {
            await saveInstagramData(INSTAGRAM_USERNAME, freshInstagram);
            setInstagramStats(freshInstagram);
          }
        }
      } catch (error) {
        console.error("Instagram stats error:", error);
      }
      setIsLoadingStats(false);
    };

    loadStats();
  }, []);

  const formatNumber = (num) => {
    if (num === null || num === undefined || num === '') return '--';
    const number = Number(num);
    if (number >= 1e9) return `${(number / 1e9).toFixed(1)}B`;
    if (number >= 1e6) return `${(number / 1e6).toFixed(1)}M`;
    if (number >= 1e3) return `${(number / 1e3).toFixed(0)}K`;
    return number.toString();
  };

  return (
    <div className={styles.main}>
      <HeroSection ytStats={ytStats} instagramStats={instagramStats} />

      {/* Added ref and base reveal class */}
      <div ref={founderRef} className={`${styles.founderSection} ${styles.revealUp}`}>
        <div className={styles.title}>
          <span className={styles.titleText}>Founder & Creator</span>
          <span className={styles.titleInfo}>Faisal Khan — the visionary behind FasBeam, caradvice, and FK-R.</span>
        </div>
        <div className={styles.innerDiv}>
          <div className={styles.profilePic}>
            <img src={profilePic} alt="Faisal Khan" />
          </div>
          <div className={styles.aboutDiv}>
            <span className={styles.aboutInfo}>
              I started MotorBeam.com back in 2008 while still pursuing my graduation. What began as a small passion project soon grew into one of India’s leading automobile portals. After completing my graduation, I pursued an MBA in Finance, but my love for automobiles never took a backseat. By mid-2012, I decided to follow my passion full-time and turned MotorBeam into a full-fledged profession.
              <br /> <br />
              Over the years, MotorBeam evolved into a trusted name among enthusiasts, and it gave me the platform to explore the automotive world more deeply. In 2017, I decided to connect with people in a more personal and authentic way—this is when I started vlogging. My style was different from the traditional review format; instead, I focused on creating raw, POV (point-of-view) content that gave viewers a real feel of cars and bikes.
              <br /> <br />
              From motovlogging to lifestyle vlogging, I’ve taken my audience on countless rides—whether it’s supercars, superbikes, vintage classics, or even tuk-tuks and trucks. Along this journey, I’ve been humbled to receive multiple top influencer awards, recognition from the auto industry, and the love of a growing community of automobile enthusiasts.
              <br /> <br />
              Today, with daily uploads, an engaged fanbase, and ventures like FK-R (Faisal Khan Racing), I continue to live my dream of blending passion, storytelling, and technology—one drive at a time.
            </span>
            <SocialBtns />
          </div>
        </div>
      </div>

      <div ref={numbersRef} className={`${styles.fasbeamNumbers} ${styles.revealScale}`}>
        <div className={styles.title}>
          <span className={styles.titleText}>FasBeam in Numbers</span>
        </div>

        <div className={styles.numContainer}>
          {/* Reusable stat card structure */}
          {[
            { icon: faYoutube, count: ytStats?.subscribers, label: "YouTube Subscribers" },
            { icon: faInstagram, count: instagramStats?.followers, label: "Instagram Followers" },
            { icon: faPlay, count: ytStats?.views, label: "Youtube Views" },
            { icon: faVideo, count: ytStats?.videoCount, label: "Car & Bike Reviews" },
          ].map((stat, index) => (
            <div key={index} className={styles.numDiv}>
              <div className={styles.num}>
                <FontAwesomeIcon className={styles.icon} icon={stat.icon} />
                {isLoadingStats ? (
                  <span className={styles.skeletonText}></span>
                ) : (
                  <span>{formatNumber(stat.count)}</span>
                )}
              </div>
              <span className={styles.numTitle}>{stat.label}</span>
            </div>
          ))}
        </div>

        <span className={styles.titleInfo}>Brands Worked With</span>
      </div>

      <AchievementsSection />
      
      <div className={styles.fasbeamNumbers}>
        <span className={styles.titleInfo}>As Seen On</span>

        <div ref={mediaRef} className={`achievement-three ${styles.revealUp}`}>
          <div className={styles.mediaLogos}>
            <img src={m1} alt="Media Logo 1" />
            <img src={m2} alt="Media Logo 2" />
            <img src={m3} alt="Media Logo 3" />
            <img src={m4} alt="Media Logo 4" />
            <img src={m5} alt="Media Logo 5" />
            <img src={m6} alt="Media Logo 6" />
          </div>
        </div>

        <div className={styles.title}>
          <span className={styles.titleText}>Latest From Youtube</span>
        </div>

        <YoutubeSection />
        <FAQSection />
      </div>
    </div>
  );
};

export default Homepage;